import { useEffect, useState } from 'react';
import { createVoiceActivityMonitor } from '../utils/voiceActivityMonitor';

let sharedContext = null;
let sharedContextUsers = 0;
let sharedContextCloseTimer = null;

const acquireAudioContext = () => {
  const AudioContextClass = window.AudioContext || window.webkitAudioContext;
  if (!AudioContextClass) return null;
  if (!sharedContext || sharedContext.state === 'closed') {
    try {
      sharedContext = new AudioContextClass({ latencyHint: 'interactive' });
    } catch {
      sharedContext = new AudioContextClass();
    }
  }
  if (sharedContextCloseTimer) {
    window.clearTimeout(sharedContextCloseTimer);
    sharedContextCloseTimer = null;
  }
  sharedContextUsers += 1;
  return sharedContext;
};

const releaseAudioContext = (context) => {
  if (!context || context !== sharedContext) return;
  sharedContextUsers = Math.max(0, sharedContextUsers - 1);
  if (sharedContextUsers > 0 || sharedContextCloseTimer) return;
  sharedContextCloseTimer = window.setTimeout(() => {
    sharedContextCloseTimer = null;
    if (sharedContextUsers > 0 || sharedContext !== context) return;
    context.close?.().catch(() => undefined);
    sharedContext = null;
  }, 10000);
};

export default function useVoiceActivity(
  stream,
  {
    fftSize = 512,
    smoothingTimeConstant = 0.25,
    minDb = -80,
    maxDb = -10,
    energyThreshold = 0.12,
    speakHold = 80,
    dropHold = 240,
    sampleIntervalMs = 50,
  } = {}
) {
  const [level, setLevel] = useState(0);
  const [isSpeaking, setIsSpeaking] = useState(false);
  useEffect(() => {
    setLevel(0);
    setIsSpeaking(false);
    if (!stream) return undefined;
    let context;
    try { context = acquireAudioContext(); } catch { return undefined; }
    if (!context) return undefined;
    const stop = createVoiceActivityMonitor({
      stream, context, fftSize, smoothingTimeConstant, minDb, maxDb,
      energyThreshold, speakHold, dropHold, sampleIntervalMs,
      onUpdate: (value) => {
        setLevel(value.level);
        setIsSpeaking(value.isSpeaking);
      },
    });
    return () => {
      stop();
      releaseAudioContext(context);
    };
  }, [stream, fftSize, smoothingTimeConstant, minDb, maxDb,
    energyThreshold, speakHold, dropHold, sampleIntervalMs]);
  return { level, isSpeaking };
}
